// Security: who can open which apps, and which records inside them.
//   Overview   the switch, roles, the demo company and recent changes
//   Role       app access (read, create, edit, delete) and record rules in plain English
//   People     roles, department and regions per person; View as and Check access from each row
//   Check      why a person can or can't reach a record, confirmed by the database; who can see a record
//   Changes    the audit trail
// The database enforces all of it (row-level security on every record), so the app, search, Courage Chat and MCP agree.
import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from "react";
import { Link, NavLink, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { CommandBar } from "../Shell";
import { ErrorBox, ago } from "../ui";
import { useLoad } from "../hooks";
import { SUITE } from "../suite";
import { reloadAccess, startViewAs, useAccess } from "./store";
import {
  AUDIT_TEXT, NOUN, plural, ruleText, sec,
  type Action, type AppGrant, type AuditRow, type Catalog, type Condition, type Person, type RoleDetail, type Rule,
} from "./secApi";
import "../maintenance/mx.css";
import "./security.css";

const ACTS: Action[] = ["read", "create", "edit", "delete"];
const ACT_LABEL: Record<Action, string> = { read: "Read", create: "Create", edit: "Edit", delete: "Delete" };
const areaName = (id: string) => SUITE.find((d) => d.id === id)?.name ?? id;

/** Every app Security can govern: the API's catalog plus the simple apps (NCR, SCAR…), in suite order. */
function useApps(cat: Catalog | null) {
  return useMemo(() => {
    if (!cat) return [];
    const out: { id: string; name: string; area: string; types: string[]; openRead: boolean }[] = [];
    for (const d of SUITE) {
      for (const a of cat.apps.filter((x) => x.area === d.id)) out.push(a);
      for (const m of d.modules) if (m.status === "live" && m.path?.startsWith("/m/") && !out.some((x) => x.id === m.id)) out.push({ id: m.id, name: m.name, area: d.id, types: [], openRead: false });
    }
    return out;
  }, [cat]);
}

function Tabs() {
  const t = (to: string, label: string, end = false) => <NavLink to={to} end={end} className={({ isActive }) => (isActive ? "on" : "")}>{label}</NavLink>;
  return (
    <nav className="sec-tabs" aria-label="Security">
      {t("/settings/security", "Overview", true)}{t("/settings/security/roles", "Roles")}{t("/settings/security/people", "People")}
      {t("/settings/security/check", "Check access")}{t("/settings/security/changes", "Changes")}
    </nav>
  );
}

function Frame({ title, sub, bar, children }: { title: ReactNode; sub?: ReactNode; bar?: ReactNode; children: ReactNode }) {
  return (
    <>
      <CommandBar status="Admin · Security">{bar}</CommandBar>
      <div className="page">
        <div className="page-title"><h1>{title}</h1>{sub && <span className="sub">{sub}</span>}</div>
        <Tabs />
        {children}
      </div>
    </>
  );
}

const Kind = ({ kind, demo }: { kind: string; demo?: boolean }) =>
  kind === "admin" ? <span className="pill info">Built in</span> : kind === "default" ? <span className="pill">Default</span> : demo ? <span className="pill warn">Demo</span> : null;

function auditLine(a: AuditRow) {
  const d = a.detail ?? {};
  const what = AUDIT_TEXT[a.action] ?? a.action;
  if (a.action === "access.set") {
    const after = d.after ? ACTS.filter((x) => d.after[x]).map((x) => ACT_LABEL[x].toLowerCase()).join(", ") : "no access";
    return <>{what}: <b>{a.target}</b> · {d.appName ?? d.app} → {after}</>;
  }
  if (a.action === "person.roles") return <>{what} for <b>{a.target}</b>: {(d.after ?? []).join(", ") || "none (Everyone)"}</>;
  if (a.action.startsWith("rule.")) return <>{what}: <b>{a.target}</b> · {d.appName ?? d.app}</>;
  const plain = ["setup", "enforce.on", "enforce.off", "demo.load", "demo.remove", "default.set"].includes(a.action);
  return <>{what}{a.target && !plain ? <>: <b>{a.target}</b></> : null}</>;
}

// ---------------------------------------------------------------------------------------------------------------
// Overview
// ---------------------------------------------------------------------------------------------------------------
export function SecurityHome() {
  const o = useLoad(() => sec.overview(), []);
  const ppl = useLoad(() => sec.people(), []);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const d = o.data;
  const act = async (key: string, fn: () => Promise<unknown>) => {
    setBusy(key); setError(null);
    try { await fn(); await Promise.all([o.reload(), ppl.reload()]); await reloadAccess(); } catch (e) { setError(e); } finally { setBusy(null); }
  };
  const demoPeople = (ppl.data ?? []).filter((p) => p.demo);

  return (
    <Frame title="Security" sub="Who can open which apps, and which records inside them. Postgres enforces it on every record, so screens, search, Courage Chat and MCP all agree."
      bar={<><Link className="cmd primary" to="/settings/security/roles?new=1">New role</Link><Link className="cmd" to="/settings/security/check">Check access</Link></>}>
      <ErrorBox error={o.error ?? error} />
      {d && (
        <section className={`card sec-switch ${d.enforced ? "on" : "off"}`}>
          <div className="sec-switch-t">
            <span className="sec-dot" aria-hidden="true" />
            <div>
              <h2>{d.enforced ? "Access control is on" : "Access control is off"}</h2>
              <p>{d.enforced
                ? "People reach only the apps and records their roles allow. Someone without a role gets the default role."
                : "Everyone signed in reaches every app and record. Set up roles, try them with View as, then turn it on."}</p>
            </div>
          </div>
          <button className={`btn ${d.enforced ? "" : "primary"}`} disabled={!!busy} onClick={() => act("switch", () => sec.settings({ enforced: !d.enforced }))}>
            {busy === "switch" ? "Saving…" : d.enforced ? "Turn off" : "Turn on"}
          </button>
        </section>
      )}

      <div className="mx-kpis sec-kpis">
        <Link className="mx-kpi" to="/settings/security/people"><span className="l">People</span><span className="v">{d?.people ?? "–"}</span></Link>
        <Link className="mx-kpi" to="/settings/security/roles"><span className="l">Roles</span><span className="v">{d?.roles.length ?? "–"}</span></Link>
        <div className="mx-kpi"><span className="l">Administrators</span><span className="v">{d?.admins ?? "–"}</span></div>
        <Link className={`mx-kpi ${d?.withoutRole ? "warn" : ""}`} to="/settings/security/people?show=none"><span className="l">Without a role</span><span className="v">{d?.withoutRole ?? "–"}</span><span className="s">They get {d?.roles.find((r) => r.id === d.defaultRoleId)?.name ?? "nothing"}</span></Link>
      </div>

      <div className="sec-how" aria-label="How access works">
        <div><b>1 · Roles open apps</b><span>Read, create, edit and delete, per app.</span></div>
        <div><b>2 · Record rules narrow them</b><span>"Opportunities they own", "accounts in their regions".</span></div>
        <div><b>3 · The database enforces it</b><span>Row-level security on every record, for every screen and AI tool.</span></div>
      </div>

      <div className="split">
        <section className="card grow">
          <div className="card-head"><h2>Roles</h2><Link to="/settings/security/roles?new=1" className="btn">New role</Link></div>
          <RoleTable roles={d?.roles ?? null} defaultRoleId={d?.defaultRoleId ?? null} />
        </section>

        <section className="card sec-demo">
          <div className="card-head"><h2>Demo company</h2>{demoPeople.length > 0 && <span className="muted" style={{ fontSize: 12 }}>{demoPeople.length} people</span>}</div>
          <div className="card-body">
            {!demoPeople.length ? (
              <>
                <p className="muted" style={{ marginTop: 0 }}>Eight people with real roles: quality, sales, maintenance, purchasing and a new hire, with accounts, deals and issues they own. Use View as to see courageMFG exactly as each of them does.</p>
                <button className="btn primary" disabled={!!busy} onClick={() => act("demo", () => sec.loadDemo())}>{busy === "demo" ? "Loading…" : "Load the demo company"}</button>
              </>
            ) : (
              <>
                <ul className="sec-people">
                  {demoPeople.map((p) => (
                    <li key={p.id}>
                      <span className="av" aria-hidden="true">{p.name.split(" ").map((x) => x[0]).join("").slice(0, 2)}</span>
                      <span className="who"><b>{p.name}</b><span>{p.roles.map((r) => r.name).join(", ") || `No role · gets ${d?.roles.find((r) => r.id === d.defaultRoleId)?.name ?? "nothing"}`}</span></span>
                      <button className="btn" onClick={() => startViewAs({ id: p.id, name: p.name })}>View as</button>
                    </li>
                  ))}
                </ul>
                {!d?.enforced && <p className="msg warn" style={{ margin: "10px 0 0" }}>View as shows each person's access even while access control is off.</p>}
                <button className="btn sec-quiet" disabled={!!busy} onClick={() => { if (confirm("Remove the demo people, their demo roles and the records they own?")) void act("demo-x", () => sec.removeDemo()); }}>
                  {busy === "demo-x" ? "Removing…" : "Remove the demo company"}
                </button>
              </>
            )}
          </div>
        </section>
      </div>

      <section className="card">
        <div className="card-head"><h2>Recent changes</h2><Link to="/settings/security/changes" className="linkish">All changes</Link></div>
        <AuditList rows={d?.recent ?? null} />
      </section>
    </Frame>
  );
}

function RoleTable({ roles, defaultRoleId }: { roles: Overview["roles"] | null; defaultRoleId: string | null }) {
  if (!roles) return <div className="empty">Loading…</div>;
  return (
    <div className="table-wrap">
      <table className="dgrid sec-roles">
        <thead><tr><th>Role</th><th>Who it's for</th><th className="n">People</th><th className="n">Apps</th><th className="n">Record rules</th></tr></thead>
        <tbody>
          {roles.map((r) => (
            <tr key={r.id}>
              <td><Link to={`/settings/security/roles/${r.id}`} className="num">{r.name}</Link> <Kind kind={r.kind} demo={r.demo} />{r.id === defaultRoleId && r.kind !== "default" && <span className="pill">Default</span>}</td>
              <td className="muted sec-desc">{r.description}</td>
              <td className="n">{r.members}</td>
              <td className="n">{r.kind === "admin" ? "All" : r.apps}</td>
              <td className="n">{r.kind === "admin" ? "–" : r.rules || "–"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
type Overview = Awaited<ReturnType<typeof sec.overview>>;

function AuditList({ rows }: { rows: AuditRow[] | null }) {
  if (!rows) return <div className="empty">Loading…</div>;
  if (!rows.length) return <div className="card-body muted">No changes yet.</div>;
  return (
    <ul className="mx-hist sec-audit">
      {rows.map((a) => <li key={a.id}><span className="when" title={new Date(a.at).toLocaleString()}>{ago(a.at)}</span><span>{auditLine(a)}<span className="muted"> · {a.actorName ?? a.actor}</span></span></li>)}
    </ul>
  );
}

// ---------------------------------------------------------------------------------------------------------------
// Roles list and new role
// ---------------------------------------------------------------------------------------------------------------
export function RolesPage() {
  const [params, setParams] = useSearchParams();
  const o = useLoad(() => sec.overview(), []);
  const cat = useLoad(() => sec.catalog(), []);
  const nav = useNavigate();
  const [name, setName] = useState(""); const [start, setStart] = useState("blank");
  const [error, setError] = useState<unknown>(null); const [busy, setBusy] = useState(false);
  const open = params.get("new") === "1";
  const create = async (e: FormEvent) => {
    e.preventDefault(); setBusy(true); setError(null);
    try {
      const body = start === "blank" ? { name } : start.startsWith("tpl:") ? { name, template: start.slice(4) } : { name, copyFrom: start.slice(5) };
      const r = await sec.createRole(body);
      nav(`/settings/security/roles/${r.id}`);
    } catch (err) { setError(err); } finally { setBusy(false); }
  };
  const tpl = cat.data?.templates.find((t) => `tpl:${t.key}` === start);
  return (
    <Frame title="Roles" sub="A role opens apps and, where it matters, narrows them to the records that fit. People can hold more than one role; their access is everything their roles allow."
      bar={<button className="cmd primary" onClick={() => setParams({ new: "1" })}>New role</button>}>
      <ErrorBox error={o.error} />
      {open && (
        <section className="card sec-new">
          <div className="card-head"><h2>New role</h2><button className="icon-btn" aria-label="Close" onClick={() => setParams({})}>✕</button></div>
          <form className="card-body" onSubmit={create}>
            <div className="field"><label className="label" htmlFor="rn">Name</label>
              <input id="rn" className="input" required maxLength={60} placeholder="e.g. Second shift lead" value={name} onChange={(e) => setName(e.target.value)} autoFocus /></div>
            <div className="field"><span className="label">Start from</span>
              <div className="sec-starts">
                <label className={`sec-start ${start === "blank" ? "on" : ""}`}><input type="radio" name="start" checked={start === "blank"} onChange={() => setStart("blank")} /><b>Blank</b><span>No apps yet. Add exactly what this role needs.</span></label>
                {cat.data?.templates.map((t) => (
                  <label key={t.key} className={`sec-start ${start === `tpl:${t.key}` ? "on" : ""}`}>
                    <input type="radio" name="start" checked={start === `tpl:${t.key}`} onChange={() => { setStart(`tpl:${t.key}`); if (!name) setName(t.name); }} />
                    <b>{t.name}</b><span>{t.description}</span>
                  </label>))}
              </div>
            </div>
            {o.data && <div className="field"><label className="label" htmlFor="cp">Or copy a role</label>
              <select id="cp" className="select" value={start.startsWith("copy:") ? start : ""} onChange={(e) => setStart(e.target.value || "blank")}>
                <option value="">–</option>{o.data.roles.filter((r) => r.kind !== "admin").map((r) => <option key={r.id} value={`copy:${r.id}`}>{r.name}</option>)}
              </select></div>}
            <ErrorBox error={error} />
            <button className="btn primary" disabled={busy || !name.trim()}>{busy ? "Creating…" : tpl ? `Create from ${tpl.name}` : "Create role"}</button>
          </form>
        </section>
      )}
      <section className="card"><RoleTable roles={o.data?.roles ?? null} defaultRoleId={o.data?.defaultRoleId ?? null} /></section>
    </Frame>
  );
}

// ---------------------------------------------------------------------------------------------------------------
// One role: app access and record rules
// ---------------------------------------------------------------------------------------------------------------
export function RolePage() {
  const { id = "" } = useParams();
  const nav = useNavigate();
  const role = useLoad(() => sec.role(id), [id]);
  const cat = useLoad(() => sec.catalog(), []);
  const ppl = useLoad(() => sec.people(), []);
  const apps = useApps(cat.data);
  const [r, setR] = useState<RoleDetail | null>(null);
  const [show, setShow] = useState<"opened" | "all">("opened");
  const [openApp, setOpenApp] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [editing, setEditing] = useState(false);
  const [nm, setNm] = useState(""); const [desc, setDesc] = useState("");
  useEffect(() => { if (role.data) { setR(role.data); if (!role.data.grants.length && role.data.kind !== "admin") setShow("all"); } }, [role.data]);
  const names = useMemo(() => Object.fromEntries((ppl.data ?? []).map((p) => [p.id, p.name])), [ppl.data]);

  const run = async (fn: () => Promise<RoleDetail>) => { setError(null); try { setR(await fn()); void reloadAccess(); } catch (e) { setError(e); } };
  if (!r) return <Frame title="Role"><ErrorBox error={role.error} /><div className="empty">Loading…</div></Frame>;

  const grant = (app: string): AppGrant => r.grants.find((g) => g.app === app) ?? { app, read: false, create: false, edit: false, delete: false };
  const toggle = (app: string, a: Action) => {
    const g = grant(app);
    const next = { read: g.read, create: g.create, edit: g.edit, delete: g.delete, [a]: !g[a] };
    if (a === "read" && !next.read) { next.create = next.edit = next.delete = false; }
    if (a !== "read" && next[a]) next.read = true;
    void run(() => sec.setApp(r.id, app, next));
  };
  const opened = new Set(r.grants.map((g) => g.app));
  const shown = apps.filter((a) => show === "all" || opened.has(a.id));
  const byArea = SUITE.map((d) => ({ d, rows: shown.filter((a) => a.area === d.id) })).filter((x) => x.rows.length);
  const memberIds = new Set(r.members.map((m) => m.id));
  const addMember = async (pid: string) => {
    const p = ppl.data?.find((x) => x.id === pid); if (!p) return;
    setError(null);
    try { await sec.setRoles(pid, [...p.roles.map((x) => x.id), r.id]); await Promise.all([role.reload(), ppl.reload()]); } catch (e) { setError(e); }
  };
  const removeMember = async (pid: string) => {
    const p = ppl.data?.find((x) => x.id === pid); if (!p) return;
    setError(null);
    try { await sec.setRoles(pid, p.roles.map((x) => x.id).filter((x) => x !== r.id)); await Promise.all([role.reload(), ppl.reload()]); } catch (e) { setError(e); }
  };
  const saveName = async (e: FormEvent) => { e.preventDefault(); await run(() => sec.updateRole(r.id, { name: nm, description: desc })); setEditing(false); };
  const remove = async () => {
    if (!confirm(`Delete ${r.name}? ${r.members.length} people lose what it gives them.`)) return;
    try { await sec.deleteRole(r.id); nav("/settings/security/roles"); } catch (e) { setError(e); }
  };

  return (
    <Frame title={<>{r.name} <Kind kind={r.kind} demo={r.demo} /></>} sub={r.description || undefined}
      bar={<>
        {r.kind !== "admin" && <button className="cmd" onClick={() => { setNm(r.name); setDesc(r.description); setEditing(true); }}>Rename</button>}
        {r.members[0] && <button className="cmd" onClick={() => startViewAs({ id: r.members[0].id, name: r.members[0].name })}>View as {r.members[0].name.split(" ")[0]}</button>}
        <Link className="cmd" to={`/settings/security/roles?new=1`}>New role</Link>
        {r.kind === "custom" && <button className="cmd" onClick={remove}>Delete role</button>}
      </>}>
      <ErrorBox error={error} />
      {editing && (
        <form className="card card-body sec-rename" onSubmit={saveName}>
          <div className="field"><label className="label" htmlFor="nm">Name</label><input id="nm" className="input" value={nm} maxLength={60} onChange={(e) => setNm(e.target.value)} /></div>
          <div className="field"><label className="label" htmlFor="ds">Who it's for</label><textarea id="ds" className="textarea" rows={2} value={desc} maxLength={400} onChange={(e) => setDesc(e.target.value)} /></div>
          <div className="btn-row"><button className="btn primary">Save</button><button type="button" className="btn" onClick={() => setEditing(false)}>Cancel</button></div>
        </form>
      )}

      <section className="card">
        <div className="card-head"><h2>People with this role</h2><span className="muted" style={{ fontSize: 12 }}>{r.members.length}{r.kind === "default" ? " · plus everyone without a role" : ""}</span></div>
        <div className="card-body sec-members">
          {r.members.map((m) => (
            <span key={m.id} className="sec-chip"><b>{m.name}</b>{m.title && <span className="muted"> · {m.title}</span>}
              <button aria-label={`Remove ${m.name}`} onClick={() => removeMember(m.id)}>✕</button></span>
          ))}
          <select className="select sec-add" value="" onChange={(e) => e.target.value && addMember(e.target.value)} aria-label="Add a person">
            <option value="">Add a person…</option>
            {(ppl.data ?? []).filter((p) => !memberIds.has(p.id)).map((p) => <option key={p.id} value={p.id}>{p.name}{p.demo ? " (demo)" : ""}</option>)}
          </select>
        </div>
      </section>

      {r.kind === "admin" ? (
        <section className="card card-body"><p style={{ margin: 0 }}><b>Administrators reach every app and every record</b>, and manage security. Their requests aren't narrowed by record rules. Keep this role to the few people who set courageMFG up.</p></section>
      ) : (
        <section className="card">
          <div className="card-head sec-grid-head">
            <h2>App access</h2>
            <div className="seg" role="group" aria-label="Show">
              <button className={show === "opened" ? "on" : ""} onClick={() => setShow("opened")}>Opened by this role ({opened.size})</button>
              <button className={show === "all" ? "on" : ""} onClick={() => setShow("all")}>All apps</button>
            </div>
          </div>
          {!byArea.length ? <div className="card-body muted">This role opens no apps yet. Choose <b>All apps</b> to add some.</div> : (
            <div className="table-wrap">
              <table className="dgrid sec-grid">
                <thead><tr><th>App</th>{ACTS.map((a) => <th key={a} className="c">{ACT_LABEL[a]}</th>)}<th>Which records</th></tr></thead>
                {byArea.map(({ d, rows }) => (
                  <tbody key={d.id}>
                    <tr className="sec-area"><th colSpan={6}>{d.name}</th></tr>
                    {rows.map((a) => {
                      const g = grant(a.id);
                      const rules = r.rules.filter((x) => x.app === a.id);
                      const ruled = a.types.length > 0;
                      return (
                        <AppRow key={a.id} app={a} g={g} rules={rules} ruled={ruled} open={openApp === a.id}
                          onToggle={(act) => toggle(a.id, act)} onOpen={() => setOpenApp(openApp === a.id ? null : a.id)}>
                          {openApp === a.id && ruled && g.read && cat.data && (
                            <RulesPanel role={r} app={a} rules={rules} cat={cat.data} names={names} people={ppl.data ?? []} onChange={run} />
                          )}
                        </AppRow>
                      );
                    })}
                  </tbody>
                ))}
              </table>
            </div>
          )}
          <p className="sec-foot muted">Create is for the app as a whole: a record that doesn't exist yet can't match a rule. Record rules narrow read, edit and delete. People always see records they raised or own in apps they can open.</p>
        </section>
      )}
    </Frame>
  );
}

function AppRow({ app, g, rules, ruled, open, onToggle, onOpen, children }: {
  app: { id: string; name: string; openRead: boolean }; g: AppGrant; rules: Rule[]; ruled: boolean; open: boolean;
  onToggle: (a: Action) => void; onOpen: () => void; children?: ReactNode;
}) {
  return (
    <>
      <tr className={g.read ? "has" : ""}>
        <td className="app">{app.name}{app.openRead && <span className="muted" title="Everyone can read it, because the navigation needs it"> · read by all</span>}</td>
        {ACTS.map((a) => (
          <td key={a} className="c">
            <input type="checkbox" className="sec-check" aria-label={`${ACT_LABEL[a]} ${app.name}`} checked={g[a]} onChange={() => onToggle(a)} />
          </td>
        ))}
        <td className="recs">
          {!g.read ? <span className="muted">–</span>
            : !ruled ? <span className="muted">Every record</span>
            : <button className={`sec-recs ${rules.length ? "ruled" : ""}`} aria-expanded={open} onClick={onOpen}>
                {rules.length ? `${rules.length} rule${rules.length === 1 ? "" : "s"}` : "Every record"}<span aria-hidden="true"> {open ? "▴" : "▾"}</span>
              </button>}
        </td>
      </tr>
      {open && children && <tr className="sec-rules-row"><td colSpan={6}>{children}</td></tr>}
    </>
  );
}

function RulesPanel({ role, app, rules, cat, names, people, onChange }: {
  role: RoleDetail; app: { id: string; name: string; types: string[] }; rules: Rule[]; cat: Catalog; names: Record<string, string>; people: Person[];
  onChange: (fn: () => Promise<RoleDetail>) => Promise<void>;
}) {
  const noun = NOUN[app.id] ?? "record";
  const [adding, setAdding] = useState(false);
  const [edit, setEdit] = useState<string | null>(null);
  return (
    <div className="sec-rules">
      <div className="sec-rules-h">
        <b>Which {app.name} records</b>
        <span className="muted">{rules.length ? "Each rule adds records. A person gets an action on a record when any rule gives it." : `${role.name} reaches every ${noun}. Add a rule to narrow it.`}</span>
      </div>
      {rules.map((x) => edit === x.id ? (
        <RuleBuilder key={x.id} app={app} cat={cat} people={people} initial={x} onCancel={() => setEdit(null)}
          onSave={async (b) => { await onChange(() => sec.updateRule(role.id, x.id, b)); setEdit(null); }} />
      ) : (
        <div key={x.id} className="sec-rule">
          <span className="txt">{ruleText(x, noun, names, cat)}</span>
          <button className="linkish" onClick={() => setEdit(x.id)}>Change</button>
          <button className="linkish" onClick={() => onChange(() => sec.deleteRule(role.id, x.id))}>Remove</button>
        </div>
      ))}
      {adding
        ? <RuleBuilder app={app} cat={cat} people={people} onCancel={() => setAdding(false)} onSave={async (b) => { await onChange(() => sec.addRule(role.id, { app: app.id, ...b })); setAdding(false); }} />
        : <div className="btn-row"><button className="btn" onClick={() => setAdding(true)}>Add a rule</button>
            {rules.length > 0 && <button className="btn sec-quiet" onClick={() => onChange(() => sec.allRecords(role.id, app.id))}>Back to every {noun}</button>}</div>}
    </div>
  );
}

const OP_LABEL: Record<Condition["op"], string> = { is_me: "is them", my_department: "is in their department", in: "is one of", not_in: "isn't one of", my_regions: "is one of their regions", is_empty: "is empty" };

function RuleBuilder({ app, cat, people, initial, onSave, onCancel }: {
  app: { id: string; name: string; types: string[] }; cat: Catalog; people: Person[]; initial?: Rule;
  onSave: (b: { actions: string[]; conditions: Condition[] }) => Promise<void>; onCancel: () => void;
}) {
  const [actions, setActions] = useState<string[]>(initial?.actions ?? ["read"]);
  const [conds, setConds] = useState<Condition[]>(initial?.conditions ?? [{ field: "owner", op: "is_me" }]);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const fields = cat.fields.filter((f) => !f.types || f.types.some((t) => app.types.includes(t)));
  const noun = NOUN[app.id] ?? "record";
  const set = (i: number, c: Partial<Condition>) => setConds(conds.map((x, j) => (j === i ? { ...x, ...c } : x)));
  const optionsFor = (c: Condition): { value: string; label: string }[] | null => {
    const f = fields.find((x) => x.field === c.field);
    if (f?.options) return f.options;
    if (c.field === "state") return (cat.states[app.types[0]] ?? []).map((v) => ({ value: v, label: v.replace(/_/g, " ").replace(/^\w/, (m) => m.toUpperCase()) }));
    if (c.field === "owner" || c.field === "creator") return people.map((p) => ({ value: p.id, label: p.name }));
    return null;
  };
  const save = async (e: FormEvent) => {
    e.preventDefault(); setBusy(true); setError(null);
    try { await onSave({ actions, conditions: conds }); } catch (err) { setError(err); } finally { setBusy(false); }
  };
  return (
    <form className="sec-builder" onSubmit={save}>
      <div className="sec-b-row">
        <span className="sec-b-l">Allow</span>
        {(["read", "edit", "delete"] as const).map((a) => (
          <label key={a} className={`chip ${actions.includes(a) ? "on" : ""}`}>
            <input type="checkbox" checked={actions.includes(a)} onChange={() => setActions(actions.includes(a) ? actions.filter((x) => x !== a) : [...actions, a])} />{ACT_LABEL[a]}
          </label>))}
        <span className="sec-b-l">on {plural(noun)}</span>
      </div>
      {conds.map((c, i) => {
        const f = fields.find((x) => x.field === c.field) ?? fields[0];
        const opts = optionsFor(c);
        return (
          <div key={i} className="sec-b-row">
            <span className="sec-b-l">{i === 0 ? "where" : "and"}</span>
            <select className="select" value={c.field} aria-label="Field" onChange={(e) => { const nf = fields.find((x) => x.field === e.target.value)!; set(i, { field: nf.field, op: nf.ops[0], values: [], entity: nf.field === "bc" ? cat.bcEntities[0].value : undefined }); }}>
              {fields.map((x) => <option key={x.field} value={x.field}>{x.label}</option>)}
            </select>
            {c.field === "bc" && (
              <select className="select" value={c.entity} aria-label="BC record type" onChange={(e) => set(i, { entity: e.target.value })}>
                {cat.bcEntities.map((x) => <option key={x.value} value={x.value}>{x.label}</option>)}
              </select>)}
            <select className="select" value={c.op} aria-label="Condition" onChange={(e) => set(i, { op: e.target.value as Condition["op"] })}>
              {f.ops.map((o) => <option key={o} value={o}>{OP_LABEL[o]}</option>)}
            </select>
            {(c.op === "in" || c.op === "not_in") && (opts ? (
              <span className="sec-vals">
                {opts.map((o) => (
                  <label key={o.value} className={`chip ${c.values?.includes(o.value) ? "on" : ""}`}>
                    <input type="checkbox" checked={!!c.values?.includes(o.value)} onChange={() => set(i, { values: c.values?.includes(o.value) ? c.values.filter((v) => v !== o.value) : [...(c.values ?? []), o.value] })} />{o.label}
                  </label>))}
              </span>
            ) : (
              <input className="input sec-vals-in" placeholder={c.field === "bc" ? "e.g. V-10, V-20" : "e.g. CA, NV"} value={(c.values ?? []).join(", ")}
                onChange={(e) => set(i, { values: e.target.value.split(",").map((v) => v.trim()) })} onBlur={() => set(i, { values: (c.values ?? []).filter(Boolean) })} />
            ))}
            {conds.length > 1 && <button type="button" className="icon-btn" aria-label="Remove condition" onClick={() => setConds(conds.filter((_, j) => j !== i))}>✕</button>}
          </div>
        );
      })}
      <div className="sec-b-row">
        <button type="button" className="linkish" onClick={() => setConds([...conds, { field: fields[0].field, op: fields[0].ops[0] }])}>And another condition</button>
        {conds.length > 0 && <button type="button" className="linkish" onClick={() => setConds([])}>Every {noun} (no conditions)</button>}
      </div>
      <p className="sec-preview">{ruleText({ actions: actions as Rule["actions"], conditions: conds }, noun, Object.fromEntries(people.map((p) => [p.id, p.name])), cat)}</p>
      <ErrorBox error={error} />
      <div className="btn-row"><button className="btn primary" disabled={busy || !actions.length}>{busy ? "Saving…" : initial ? "Save rule" : "Add rule"}</button><button type="button" className="btn" onClick={onCancel}>Cancel</button></div>
    </form>
  );
}

// ---------------------------------------------------------------------------------------------------------------
// People
// ---------------------------------------------------------------------------------------------------------------
export function PeoplePage() {
  const [params, setParams] = useSearchParams();
  const ppl = useLoad(() => sec.people(), []);
  const o = useLoad(() => sec.overview(), []);
  const [open, setOpen] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const show = params.get("show") ?? "all";
  const rows = (ppl.data ?? []).filter((p) => show === "all" || (show === "demo" ? p.demo : show === "none" ? !p.roles.length && !p.blocked : show === "blocked" ? p.blocked : true));
  const def = o.data?.roles.find((r) => r.id === o.data?.defaultRoleId)?.name;
  const save = async (fn: () => Promise<unknown>) => { setError(null); try { await fn(); await ppl.reload(); void reloadAccess(); } catch (e) { setError(e); } };
  const count = (k: string) => (ppl.data ?? []).filter((p) => (k === "demo" ? p.demo : k === "none" ? !p.roles.length && !p.blocked : p.blocked)).length;

  return (
    <Frame title="People" sub={`Everyone who has signed in. Someone without a role gets ${def ?? "the default role"}. Department and regions feed record rules like "owned by their department" and "in their regions".`}>
      <ErrorBox error={ppl.error ?? error} />
      <div className="views sec-filter" role="tablist">
        {[["all", "Everyone"], ["none", `Without a role (${count("none")})`], ["demo", `Demo (${count("demo")})`], ["blocked", `Blocked (${count("blocked")})`]].map(([k, l]) => (
          <button key={k} className={show === k ? "on" : ""} onClick={() => setParams(k === "all" ? {} : { show: k })}>{l}</button>))}
      </div>
      <section className="card">
        <div className="table-wrap">
          <table className="dgrid sec-ppl">
            <thead><tr><th>Person</th><th>Roles</th><th>Department</th><th>Regions</th><th>Last seen</th><th aria-label="Actions" /></tr></thead>
            <tbody>
              {rows.map((p) => (
                <PersonRow key={p.id} p={p} open={open === p.id} onOpen={() => setOpen(open === p.id ? null : p.id)} roles={o.data?.roles ?? []} def={def} onSave={save} />
              ))}
              {!rows.length && <tr><td colSpan={6} className="muted">Nobody here.</td></tr>}
            </tbody>
          </table>
        </div>
      </section>
    </Frame>
  );
}

function PersonRow({ p, open, onOpen, roles, def, onSave }: { p: Person; open: boolean; onOpen: () => void; roles: Overview["roles"]; def?: string; onSave: (fn: () => Promise<unknown>) => Promise<void> }) {
  const [title, setTitle] = useState(p.title ?? ""); const [dept, setDept] = useState(p.department ?? ""); const [regions, setRegions] = useState(p.regions.join(", "));
  const me = useAccess();
  useEffect(() => { setTitle(p.title ?? ""); setDept(p.department ?? ""); setRegions(p.regions.join(", ")); }, [p]);
  const has = new Set(p.roles.map((r) => r.id));
  const toggleRole = (id: string) => onSave(() => sec.setRoles(p.id, has.has(id) ? [...has].filter((x) => x !== id) : [...has, id]));
  return (
    <>
      <tr className={p.blocked ? "sec-blocked" : ""}>
        <td><button className="linkish sec-name" aria-expanded={open} onClick={onOpen}><b>{p.name}</b></button>{p.demo && <span className="pill warn sm" style={{ marginLeft: 6 }}>Demo</span>}{p.blocked && <span className="pill danger sm" style={{ marginLeft: 6 }}>Blocked</span>}
          <div className="muted sec-sub">{p.title ?? p.email ?? ""}</div></td>
        <td>{p.roles.length ? p.roles.map((r) => <span key={r.id} className="sec-role">{r.name}</span>) : <span className="muted">None · gets {def ?? "nothing"}</span>}</td>
        <td>{p.department ?? <span className="muted">–</span>}</td>
        <td>{p.regions.length ? p.regions.join(", ") : <span className="muted">–</span>}</td>
        <td className="muted" style={{ whiteSpace: "nowrap" }}>{p.demo ? "Demo" : ago(p.lastSeenAt)}</td>
        <td className="acts">
          {p.id !== me?.person.id && <button className="btn" onClick={() => startViewAs({ id: p.id, name: p.name })} title={p.demo ? "See and act as them" : "See courageMFG as they do (look only)"}>View as</button>}
          <Link className="btn" to={`/settings/security/check?user=${encodeURIComponent(p.id)}`}>Check</Link>
        </td>
      </tr>
      {open && (
        <tr className="sec-rules-row"><td colSpan={6}>
          <div className="sec-person">
            <div>
              <div className="sec-b-l" style={{ marginBottom: 6 }}>Roles</div>
              <div className="sec-rolepick">
                {roles.map((r) => (
                  <label key={r.id} className={`chip ${has.has(r.id) ? "on" : ""}`}><input type="checkbox" checked={has.has(r.id)} onChange={() => toggleRole(r.id)} />{r.name}</label>))}
              </div>
            </div>
            <form className="sec-person-f" onSubmit={(e) => { e.preventDefault(); void onSave(() => sec.updatePerson(p.id, { title: title || null, department: dept || null, regions: regions.split(/[,\s]+/).filter(Boolean) })); }}>
              <div className="field"><label className="label" htmlFor={`t-${p.id}`}>Title</label><input id={`t-${p.id}`} className="input" value={title} onChange={(e) => setTitle(e.target.value)} /></div>
              <div className="field"><label className="label" htmlFor={`d-${p.id}`}>Department</label><input id={`d-${p.id}`} className="input" placeholder="e.g. Sales" value={dept} onChange={(e) => setDept(e.target.value)} /></div>
              <div className="field"><label className="label" htmlFor={`r-${p.id}`}>Regions</label><input id={`r-${p.id}`} className="input" placeholder="e.g. CA, NV, OR" value={regions} onChange={(e) => setRegions(e.target.value)} /><span className="hint">State or province codes, as on accounts.</span></div>
              <div className="btn-row">
                <button className="btn primary">Save</button>
                {p.id !== me?.person.id && <button type="button" className="btn sec-quiet" onClick={() => onSave(() => sec.updatePerson(p.id, { blocked: !p.blocked }))}>{p.blocked ? "Unblock" : "Block"}</button>}
              </div>
            </form>
          </div>
        </td></tr>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------------------------------------------
// Check access
// ---------------------------------------------------------------------------------------------------------------
export function CheckPage() {
  const [params, setParams] = useSearchParams();
  const ppl = useLoad(() => sec.people(), []);
  const cat = useLoad(() => sec.catalog(), []);
  const user = params.get("user") ?? ""; const record = params.get("record") ?? "";
  const [u, setU] = useState(user); const [rec, setRec] = useState(record);
  useEffect(() => { setU(user); setRec(record); }, [user, record]);
  const ex = useLoad(() => (user && record ? sec.explain(user, record) : Promise.resolve(null)), [user, record]);
  const who = useLoad(() => (record ? sec.recordAccess(record) : Promise.resolve(null)), [record]);
  const names = useMemo(() => Object.fromEntries((ppl.data ?? []).map((p) => [p.id, p.name])), [ppl.data]);
  const go = (e: FormEvent) => { e.preventDefault(); const q: Record<string, string> = {}; if (u) q.user = u; if (rec.trim()) q.record = rec.trim().toUpperCase(); setParams(q); };
  const x = ex.data;

  return (
    <Frame title="Check access" sub="Pick a person and a record. See what they can do with it, and why. The answer is checked against the database, the same way every request is enforced.">
      <form className="card card-body sec-checkform" onSubmit={go}>
        <div className="field"><label className="label" htmlFor="cu">Person</label>
          <select id="cu" className="select" value={u} onChange={(e) => setU(e.target.value)}>
            <option value="">Pick a person…</option>
            {(ppl.data ?? []).map((p) => <option key={p.id} value={p.id}>{p.name}{p.title ? ` · ${p.title}` : ""}</option>)}
          </select></div>
        <div className="field"><label className="label" htmlFor="cr">Record</label>
          <input id="cr" className="input mono" placeholder="e.g. OPP-00003, ISS-00002" value={rec} onChange={(e) => setRec(e.target.value)} /></div>
        <button className="btn primary" disabled={!rec.trim()}>Check</button>
      </form>
      <ErrorBox error={ex.error ?? who.error} />

      {x && (
        <section className="card">
          <div className="card-head"><h2>{x.person.name} and {x.record.number}</h2>
            {x.database && <span className={`sec-db ${x.agrees ? "ok" : "bad"}`}>{x.agrees ? "✓ Confirmed by the database" : "✕ The database answered differently"}</span>}</div>
          <div className="card-body">
            <p className="muted" style={{ marginTop: 0 }}>{x.record.title} · {x.record.type.replace(/_/g, " ")} · {x.record.state.replace(/_/g, " ")}{x.record.owner ? ` · owned by ${names[x.record.owner] ?? x.record.owner}` : ""}{x.record.region ? ` · ${x.record.region}` : ""}
              <br />{x.person.name}: {x.person.admin ? "Administrator" : x.person.roles.join(", ") || "no role"}{x.person.implicitDefault ? " (default)" : ""}{x.person.department ? ` · ${x.person.department}` : ""}{x.person.regions.length ? ` · regions ${x.person.regions.join(", ")}` : ""}</p>
            <div className="sec-verdicts">
              {ACTS.map((a) => {
                const v = x.verdicts[a];
                return (
                  <div key={a} className={`sec-verdict ${v.allowed ? "yes" : "no"}`}>
                    <div className="h"><span className="mark" aria-hidden="true">{v.allowed ? "✓" : "✕"}</span><b>{ACT_LABEL[a]}</b><span className="sr-only">{v.allowed ? "allowed" : "not allowed"}</span></div>
                    <p>{v.reason}</p>
                    {v.allowed && v.via.some((s) => s.ruleId) && <ul>{v.via.filter((s) => s.ruleId).map((s, i) => <li key={i}><b>{s.role}</b>: {ruleText({ actions: [a as "read"], conditions: s.conditions }, x.record.type.replace(/_/g, " "), names, cat.data ?? undefined)}</li>)}</ul>}
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {who.data && (
        <section className="card">
          <div className="card-head"><h2>Who can see {who.data.record.number}</h2><span className="muted" style={{ fontSize: 12 }}>{who.data.people.filter((p) => p.read).length} of {who.data.people.length} people</span></div>
          <div className="table-wrap">
            <table className="dgrid">
              <thead><tr><th>Person</th><th>Roles</th><th className="c">Read</th><th className="c">Edit</th><th className="c">Delete</th><th>Why</th></tr></thead>
              <tbody>{[...who.data.people].sort((a, b) => Number(b.read) - Number(a.read) || Number(b.edit) - Number(a.edit) || a.name.localeCompare(b.name)).map((p) => (
                <tr key={p.id} className={p.read ? "" : "sec-dim"}>
                  <td><Link to={`/settings/security/check?user=${encodeURIComponent(p.id)}&record=${who.data!.record.number}`}>{p.name}</Link>{p.demo && <span className="pill warn sm" style={{ marginLeft: 6 }}>Demo</span>}</td>
                  <td className="muted">{p.roles.join(", ") || "–"}</td>
                  {(["read", "edit", "delete"] as const).map((a) => <td key={a} className={`c sec-yn ${p[a] ? "y" : "n"}`}>{p[a] ? "✓" : "–"}</td>)}
                  <td className="muted sec-desc">{p.why}</td>
                </tr>))}</tbody>
            </table>
          </div>
        </section>
      )}
    </Frame>
  );
}

// ---------------------------------------------------------------------------------------------------------------
// Changes
// ---------------------------------------------------------------------------------------------------------------
export function ChangesPage() {
  const a = useLoad(() => sec.audit(300), []);
  return (
    <Frame title="Changes" sub="Every change to roles, access, record rules, people and the switch: who, when, and what it was before. This log can be added to, never changed.">
      <ErrorBox error={a.error} />
      <section className="card"><AuditList rows={a.data} /></section>
    </Frame>
  );
}
